import { PASSED, FAILED, NO_DEVICE_FOUND } from 'constants/ReadinessCheck';
import { shouldHaveFeature } from '@studio/feature.flags';
import { BROADCAST_TYPE } from '../constants/SearchParams';
import { DIY_EMBEDDED, WEBINAR } from '../constants/BroadcastTypes';
import { GRAY, GREEN, RED } from '../constants/BadgeModes';
import {
  isRecording as _isRecording,
  isStreaming as _isStreaming
} from '@shoflo/studio-redux/utils';

const isCheckPassed = element => PASSED === element;
const isCheckFailed = element =>
  element === FAILED || element === NO_DEVICE_FOUND;

export function getReadinessCheckStatus(readinessCheck) {
  const allReadinessCheckResult = { ...readinessCheck };
  const requiredReadinessCheckResult = { ...readinessCheck };
  delete requiredReadinessCheckResult['screenShare'];
  const allCheckResultList = Object.values(readinessCheck);
  const requiredResultList = Object.values(requiredReadinessCheckResult);

  const allChecksPassed = allCheckResultList.every(isCheckPassed);
  const allRequiredChecksPassed =
    requiredResultList.every(isCheckPassed) &&
    allReadinessCheckResult['screenShare'] !== 'waiting';
  const oneOrAllChecksFailed = requiredResultList.some(isCheckFailed);

  return { allChecksPassed, allRequiredChecksPassed, oneOrAllChecksFailed };
}

export function getUserFirstNameLastName(user) {
  let userFirstName = null;
  let userLastName = null;

  if (user) {
    userFirstName = user.firstName;
    userLastName = user.lastName;
  }

  return { userFirstName, userLastName };
}

/**
 * gets the presenter setting from limitedBroadcast
 * @param settingName
 * @param presenterSettings
 * @returns {*|boolean}
 */
export const isPresenterSettingEnabled = (settingName, presenterSettings) => {
  return presenterSettings && presenterSettings[settingName];
};

/**
 * checks whether branding is enabled or not
 * @param brandingSetting
 * @param queryParams
 * @returns {*|boolean}
 */
export const isBrandingEnabled = (brandingSetting, queryParams) => {
  const isCameraOffStateEnabled = shouldHaveFeature('branding.cameraOffState');
  return (
    isCameraOffStateEnabled && (brandingSetting || queryParams.get('branding'))
  );
};

/**
 * Checks whether presenter should join without knock
 * @param broadcast
 * @returns {*|boolean}
 */
export const shouldJoinWithoutKnock = (broadcast = null) => {
  const queryParams = new URLSearchParams(window.location.search);

  const broadcastType =
    queryParams.get(BROADCAST_TYPE) ?? broadcast?.meta?.broadcastType ?? '';

  return (
    shouldHaveFeature('studio.requirePresenterAuth') &&
    [WEBINAR, DIY_EMBEDDED].includes(broadcastType)
  );
};

/**
 * Computes the current stream status for a presenter.
 *
 * When `joinWithoutKnockEnabled` is true, this function simply returns the
 * provided `streamStatus` object. When it is false, the status is derived
 * from the `endpoints` and `recordings` arrays using the shared
 * `_isStreaming` and `_isRecording` helpers.
 *
 * @param {Array<Object>} endpoints - List of endpoint objects used to infer streaming state when joinWithoutKnockEnabled is false.
 * @param {Array<Object>} recordings - List of recording objects used to infer recording state when joinWithoutKnockEnabled is false.
 * @param {{ isStreaming: boolean, isRecording: boolean }} streamStatus - The current stream status coming from the caller/UI state.
 * @param {boolean} joinWithoutKnockEnabled - Whether the join-without-knock flow is enabled for the current presenter.
 * @returns {{ isStreaming: boolean, isRecording: boolean }} The effective stream status for badge rendering and UI.
 */
export const getStreamStatus = (
  endpoints,
  recordings,
  streamStatus,
  joinWithoutKnockEnabled
) => {
  if (joinWithoutKnockEnabled) {
    return {
      isStreaming: streamStatus.isStreaming,
      isRecording: streamStatus.isRecording
    };
  }

  const isStreaming = endpoints.some(_isStreaming);
  const isRecording = recordings.some(_isRecording);

  return {
    isStreaming,
    isRecording
  };
};

/**
 * Returns the list of presenter status badges based on streaming/recording flags and local attendee presence.
 *
 * @param {boolean} isStreaming - Whether the presenter is currently streaming.
 * @param {boolean} isRecording - Whether the presenter is currently being recorded.
 * @param {boolean} isLocalAttendeeLive - Whether the local attendee is currently live/on stage.
 * @returns {Array<{ caption: string, mode: string }>} List of badge objects to be rendered for the presenter.
 */
export const getPresenterBadges = (
  isStreaming,
  isRecording,
  isLocalAttendeeLive
) => {
  const badges = [];

  if (!isStreaming) {
    badges.push({ caption: 'Not streaming', mode: GRAY });
  }

  if (isRecording && !isStreaming) {
    badges.push({ caption: 'Recording', mode: RED });
  }

  if (isStreaming && !isRecording) {
    badges.push({ caption: 'Streaming', mode: RED });
  }

  if (isRecording && isStreaming) {
    badges.push({
      caption: 'Streaming and Recording',
      mode: RED
    });
  }
  if (isLocalAttendeeLive) {
    badges.push({ caption: "You're on stage", mode: GREEN });
  }

  return badges;
};
